import { Button, ButtonType } from '@ardoq/button';
import { ButtonWithDropdown, DropdownItem } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { type I18nLocale, t } from '@ardoq/i18n';
import { tourAnchorIds } from '@ardoq/product-tours';

type VisualizeAsButtonProps = {
  selectedCount: number;
  visualizeButtonOptions: DropdownItem[];
  totalNumberOfRows: number;
  isUserContributor: boolean;
  openOverviewViewpoint: VoidFunction;
  i18nLocale: I18nLocale;
};

export const VisualizeButton = ({
  selectedCount,
  visualizeButtonOptions,
  totalNumberOfRows,
  isUserContributor,
  openOverviewViewpoint,
  i18nLocale,
}: VisualizeAsButtonProps) => {
  if (isUserContributor) {
    return (
      <Button
        onClick={openOverviewViewpoint}
        isDisabled={selectedCount < 1 || selectedCount > 100}
        data-tooltip-text={getTooltipText(
          selectedCount,
          isUserContributor,
          i18nLocale
        )}
        buttonType={ButtonType.TERTIARY}
        data-tour-id={tourAnchorIds.inventoryVisualizeButton}
      >
        {t(
          i18nLocale,
          'inventory:interactiveRow.visualizeSelection',
          'Visualize selection'
        )}
      </Button>
    );
  }
  return (
    <ButtonWithDropdown
      buttonType={getButtonType(selectedCount)}
      isDisabled={totalNumberOfRows === 0 || selectedCount > 100}
      tooltipText={getTooltipText(selectedCount, isUserContributor, i18nLocale)}
      options={visualizeButtonOptions}
      maxDropdownSize={DropdownSize.M}
      data-tour-id={tourAnchorIds.inventoryVisualizeButton}
    >
      {getButtonText(selectedCount, i18nLocale)}
    </ButtonWithDropdown>
  );
};

const getButtonType = (selectedCount: number) => {
  if (selectedCount === 0) {
    return ButtonType.TERTIARY;
  }
  return ButtonType.PRIMARY;
};

const getButtonText = (selectedCount: number, i18nLocale: I18nLocale) => {
  if (selectedCount === 0) {
    return t(
      i18nLocale,
      'inventory:interactiveRow.visualizeAll',
      'Visualize all'
    );
  }
  return t(
    i18nLocale,
    'inventory:interactiveRow.visualizeSelected',
    'Visualize selected'
  );
};

const getTooltipText = (
  selectedCount: number,
  isUserContributor: boolean,
  i18nLocale: I18nLocale
) => {
  if (selectedCount === 0 && isUserContributor) {
    return t(
      i18nLocale,
      'inventory:interactiveRow.selectComponentsToVisualize',
      'Select components to visualize'
    );
  }
  if (selectedCount > 100) {
    return t(
      i18nLocale,
      'inventory:interactiveRow.visualizeLimitTooltip',
      'You can only visualize up to 100 components'
    );
  }
  return undefined;
};
